export class AppleAuthService {
  static getConfig() {
    return {
      clientId:
        process.env.REACT_APP_CONNEXION_APPLE_CLIENT_ID ||
        process.env.REACT_APP_APPLE_APP_ID,
      redirectURI: process.env.REACT_APP_CONNEXION_APPLE_REDIRECT_URI,
      scope: "name email",
      usePopup: true,
    };
  }

  static ensureConfigured() {
    const { clientId, redirectURI } = this.getConfig();

    if (!clientId || !redirectURI) {
      const error = new Error("Apple authentication is not configured.");
      error.code = "APPLE_NOT_CONFIGURED";
      throw error;
    }
  }

  static loadSdk() {
    if (typeof window === "undefined" || typeof document === "undefined") {
      const error = new Error("Apple SDK is only available in the browser.");
      error.code = "APPLE_SDK_UNAVAILABLE";
      throw error;
    }

    if (window.AppleID?.auth) {
      return Promise.resolve(window.AppleID);
    }

    if (this.sdkPromise) {
      return this.sdkPromise;
    }

    this.sdkPromise = new Promise((resolve, reject) => {
      const resolveSdk = () => {
        if (window.AppleID?.auth) {
          resolve(window.AppleID);
          return;
        }

        const error = new Error("Apple SDK failed to initialize.");
        error.code = "APPLE_SDK_UNAVAILABLE";
        reject(error);
      };

      const rejectSdk = () => {
        const error = new Error("Unable to load Apple SDK.");
        error.code = "APPLE_SDK_LOAD_FAILED";
        reject(error);
      };

      const existingScript = document.querySelector(
        'script[data-apple-auth-sdk="true"]',
      );

      if (existingScript) {
        existingScript.addEventListener("load", resolveSdk, { once: true });
        existingScript.addEventListener("error", rejectSdk, { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src =
        "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
      script.async = true;
      script.defer = true;
      script.dataset.appleAuthSdk = "true";
      script.onload = resolveSdk;
      script.onerror = rejectSdk;

      document.head.appendChild(script);
    });

    return this.sdkPromise;
  }

  // Décoder JWT avec meilleure gestion d'erreurs (inspiré du code Angular)
  static decodeJwt(token) {
    if (!token || token.split(".").length < 2) {
      return null;
    }

    try {
      const payload = token.split(".")[1]; // base64url
      // Normalise en base64
      const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
      const padded = base64 + "===".slice((base64.length + 3) % 4);
      
      try {
        const json = atob(padded)
          .split("")
          .map((c) => c.charCodeAt(0))
          .reduce((acc, c) => acc + String.fromCharCode(c), "");
        return JSON.parse(decodeURIComponent(json));
      } catch {
        // Fallback si decodeURIComponent pose souci
        const bin = atob(padded);
        const bytes = new Uint8Array([...bin].map((ch) => ch.charCodeAt(0)));
        const text = new TextDecoder().decode(bytes);
        return JSON.parse(text);
      }
    } catch {
      return null;
    }
  }

  static decodeJwtPayload(token) {
    return this.decodeJwt(token);
  }

  static normalizeResponse(response) {
    const normalizedResponse = response?.result || response;
    const idToken = normalizedResponse?.authorization?.id_token;
    const payload = this.decodeJwt(idToken);
    const user = normalizedResponse?.user || {};
    const name = user?.name || {};

    // Identifiants de base
    let email = (user?.email || payload?.email || "").toLowerCase() || "";
    let userId = payload?.sub || user?.email || "";

    // Si pas d'email, c'est une erreur
    if (!email) {
      throw new Error("Apple account email is required.");
    }

    // Prénoms/noms avec valeurs par défaut (comme en Angular)
    let firstname = name?.firstName || payload?.given_name || "Utilisateur";
    let lastname = name?.lastName || payload?.family_name || "Apple";

    return {
      socialId: userId,
      socialEmail: email,
      socialType: "apple",
      statusEmail: Boolean(payload?.email_verified ?? user?.email),
      firstname,
      lastname,
      // Ajouter tokens si disponibles (utile pour futurs besoins backend)
      idToken: idToken || "",
      accessToken: normalizedResponse?.authorization?.access_token || "",
    };
  }

  static async initialize() {
    this.ensureConfigured();
    const AppleID = await this.loadSdk();
    const config = this.getConfig();
    const configKey = `${config.clientId}|${config.redirectURI}`;

    if (this.initializedConfigKey !== configKey) {
      AppleID.auth.init({
        clientId: config.clientId,
        scope: config.scope,
        redirectURI: config.redirectURI,
        usePopup: config.usePopup,
      });
      this.initializedConfigKey = configKey;
    }

    return AppleID;
  }

  static async signIn() {
    const AppleID = await this.initialize();

    try {
      const response = await AppleID.auth.signIn();
      const normalizedResponse = response?.result || response;

      // Vérifier les erreurs possibles (inspiré du code Angular)
      const errMsg =
        normalizedResponse?.error?.message ||
        response?.error?.message ||
        (typeof normalizedResponse?.message === "string"
          ? normalizedResponse.message
          : "");

      if (errMsg) throw new Error(errMsg);

      if (!normalizedResponse?.authorization?.id_token) {
        throw new Error("Apple returned an empty response.");
      }

      // Traiter la réponse et normaliser
      const socialData = this.normalizeResponse(normalizedResponse);

      // Vérification final d'email (comme en Angular)
      if (!socialData.socialEmail) {
        const error = new Error("Apple account email is required.");
        error.code = "APPLE_MISSING_EMAIL";
        throw error;
      }

      return socialData;
    } catch (error) {
      console.error("Apple sign-in failed", {
        code: error?.code || null,
        message: error?.message || String(error),
      });

      // Déterminer le type d'erreur (inspiré du code Angular)
      const cancellationValues = [
        error?.code,
        error?.error,
        error?.message,
        error?.error?.message,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (
        cancellationValues.includes("cancel") ||
        cancellationValues.includes("popup_closed")
      ) {
        const cancelledError = new Error(
          "Apple sign-in was cancelled by user."
        );
        cancelledError.code = "APPLE_CANCELLED";
        throw cancelledError;
      }

      // Préserver les erreurs avec code
      if (error?.code) {
        throw error;
      }

      // Wrapper avec code si pas présent
      const signInError = new Error(error?.message || "Apple sign-in failed.");
      signInError.code = "APPLE_SIGNIN_FAILED";
      throw signInError;
    }
  }
}

AppleAuthService.sdkPromise = null;
AppleAuthService.initializedConfigKey = null;

export default AppleAuthService;
